import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { urls } from "@selector/shared";
import type { createBrowserRouter } from "react-router-dom";
import type { Store } from "redux";

import WebSocketClient from "@/modules/notifications/WebSocketClient.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxAction } from "@/redux/redux-utils.ts";

// In order to have access to the Redux store and the ApolloClient from everywhere.
// We can't load them from @/setup.ts, because it's the initial setup process.

class Setup<S, C, R> {
  #store: S | undefined;
  #client: C | undefined;
  #router: R | undefined;

  getStore = () => {
    return this.#store;
  };

  setStore = (store: S) => {
    if (this.#store != null) {
      throw new Error("The Redux Store has already been set");
    }

    this.#store = store;
  };

  getClient = () => {
    return this.#client;
  };

  setClient = (client: C) => {
    if (this.#client != null) {
      throw new Error("The Apollo Client has already been set");
    }

    this.#client = client;
  };

  getRouter = () => {
    return this.#router;
  };

  setRouter = (router: R) => {
    if (this.#router != null) {
      throw new Error("The Router has already been set");
    }

    this.#router = router;
  };
}

// @ts-ignore
const setup = new Setup<
  Store<IAppState, ReduxAction<unknown>>,
  ApolloClient<NormalizedCacheObject>,
  ReturnType<typeof createBrowserRouter>
>();
const { getClient, getStore, getRouter } = setup;

// reconnectionAttempts: -1 means try infinitely
export const webSocketClient = new WebSocketClient(urls.ws, {
  timeout: 60000,
  reconnect: true,
  reconnectionAttempts: -1,
});

export default setup;
export { getClient as client, getRouter as router, getStore as store };
